<template>
  <div class="cameraMosaicPanel">
    <div class="cameraMosaicPanel__header">
      <span class="cameraMosaicPanel__title">{{ title }}</span>
      <div class="cameraMosaicPanel__headerActions">
        <el-popover
          v-if="hiddenChannels.length"
          placement="bottom-end"
          trigger="click"
          width="220"
        >
          <div class="cameraMosaicPanel__hiddenList">
            <div
              v-for="channel in hiddenChannelDescriptors"
              :key="channel.index"
              class="cameraMosaicPanel__hiddenItem"
            >
              <span>{{ channel.name }}</span>
              <el-button
                type="text"
                icon="fas fa-eye"
                @click="showChannel(channel.index)"
              >{{ $t('vehicleDetail.camera_show') }}</el-button>
            </div>
            <el-button type="text" @click="showAll">{{ $t('vehicleDetail.mosaic_show_all') }}</el-button>
          </div>
          <el-button
            slot="reference"
            icon="fas fa-eye-slash"
            type="text"
            class="cameraMosaicPanel__hiddenToggle"
          >{{ $t('vehicleDetail.mosaic_hidden_cameras', { count: hiddenChannels.length }) }}</el-button>
        </el-popover>
        <el-button
          icon="el-icon-close"
          type="text"
          class="cameraMosaicPanel__close"
          @click="close"
        ></el-button>
      </div>
    </div>
    <div class="cameraMosaicPanel__body">
      <div
        v-if="visibleMountedChannels.length"
        class="cameraMosaicPanel__grid"
        :class="[
          `cameraMosaicPanel__grid--count-${visibleChannels.length}`,
          { 'cameraMosaicPanel__grid--expanded': expandedChannel }
        ]"
      >
        <camera-tile
          v-for="channel in visibleMountedChannels"
          :key="channel.index"
          class="cameraMosaicPanel__tile"
          :class="{
            'cameraMosaicPanel__tile--hidden': expandedChannel && expandedChannel !== channel.index
          }"
          :device="device"
          :channel="channel.index"
          :name="channel.name"
          :muted="unmutedChannel !== channel.index"
          :expanded="expandedChannel === channel.index"
          @toggle-audio="toggleAudio"
          @toggle-expand="toggleExpand"
          @toggle-hide="hideChannel"
        ></camera-tile>
      </div>
      <el-alert
        v-else-if="channels.length && hiddenChannels.length"
        :title="$t('vehicleDetail.mosaic_all_hidden')"
        type="info"
        :closable="false"
        show-icon
      >
        <el-button type="text" @click="showAll">{{ $t('vehicleDetail.mosaic_show_all') }}</el-button>
      </el-alert>
      <el-alert
        v-else
        :title="$t('vehicleDetail.camera_unavailable')"
        type="info"
        :closable="false"
        show-icon
      ></el-alert>
    </div>
  </div>
</template>

<script>
import CameraTile from '@/components/CameraTile.vue'
import { getCameraChannels } from '@/utils/cameras'

const REVEAL_INTERVAL = 500

export default {
  name: 'CameraMosaicPanel',
  components: { CameraTile },
  props: {
    device: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      mountedChannels: [],
      expandedChannel: null,
      unmutedChannel: null,
      revealTimer: null
    }
  },
  computed: {
    channels() {
      return getCameraChannels(this.device)
    },
    title() {
      return this.$t('vehicleDetail.mosaic_title', { device: this.device.name })
    },
    hiddenChannels: {
      get() {
        return this.$store.state.settings.hiddenCameraChannels[this.device.id] || []
      },
      set(channels) {
        this.$store.dispatch('settings/setHiddenCameraChannels', { deviceId: this.device.id, channels })
      }
    },
    visibleChannels() {
      return this.channels.filter(channel => !this.hiddenChannels.includes(channel.index))
    },
    visibleMountedChannels() {
      return this.mountedChannels.filter(channel => !this.hiddenChannels.includes(channel.index))
    },
    hiddenChannelDescriptors() {
      return this.channels.filter(channel => this.hiddenChannels.includes(channel.index))
    }
  },
  watch: {
    'device.id': 'reveal'
  },
  mounted() {
    this.reveal()
  },
  beforeDestroy() {
    clearTimeout(this.revealTimer)
  },
  methods: {
    reveal() {
      clearTimeout(this.revealTimer)
      this.revealTimer = null
      this.mountedChannels = []
      this.expandedChannel = null
      this.unmutedChannel = null
      this.revealNext(0)
    },
    revealNext(index) {
      if (index >= this.channels.length) {
        return
      }
      this.mountedChannels.push(this.channels[index])
      this.revealTimer = setTimeout(() => this.revealNext(index + 1), REVEAL_INTERVAL)
    },
    toggleAudio(channel) {
      this.unmutedChannel = this.unmutedChannel === channel ? null : channel
    },
    toggleExpand(channel) {
      this.expandedChannel = this.expandedChannel === channel ? null : channel
    },
    hideChannel(channel) {
      if (!this.hiddenChannels.includes(channel)) {
        this.hiddenChannels = [...this.hiddenChannels, channel]
      }
      if (this.expandedChannel === channel) {
        this.expandedChannel = null
      }
      if (this.unmutedChannel === channel) {
        this.unmutedChannel = null
      }
    },
    showChannel(channel) {
      this.hiddenChannels = this.hiddenChannels.filter(index => index !== channel)
    },
    showAll() {
      this.hiddenChannels = []
    },
    close() {
      this.$store.dispatch('transient/toggleMosaic', this.device.id)
    }
  }
}
</script>

<style lang="scss">
  .cameraMosaicPanel {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #000;
  }

  .cameraMosaicPanel__header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 0 10px;
    color: #fff;
  }

  .cameraMosaicPanel__title {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cameraMosaicPanel__headerActions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .cameraMosaicPanel__hiddenToggle.el-button {
    color: #fff;
    font-size: 12px;
    white-space: nowrap;
  }

  .cameraMosaicPanel__close.el-button {
    color: #fff;
  }

  .cameraMosaicPanel__hiddenList {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cameraMosaicPanel__hiddenItem {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .cameraMosaicPanel__body {
    flex: 1 1 auto;
    min-height: 0;
  }

  .cameraMosaicPanel__grid {
    display: grid;
    gap: 4px;
    background: #000;
    height: 100%;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 1fr;

    &--count-1 {
      grid-template-columns: 1fr;
    }

    &--count-5,
    &--count-6 {
      grid-template-columns: repeat(3, 1fr);
    }

    &--expanded {
      display: block;
    }
  }

  .cameraMosaicPanel__tile {
    width: 100%;
    height: 100%;
  }

  .cameraMosaicPanel__tile--hidden {
    display: none;
  }
</style>
